import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Upload, FileSpreadsheet, Users, Baby, DollarSign, Download, Check, X, 
  AlertCircle, Loader2, Plus, Trash2, ArrowRight, History, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/config/api';

const DataMigrationPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('import');
  
  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Data Migration</h1>
        <p className="text-muted-foreground mt-1">
          Import data from CSV/Excel or add entries in bulk
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="import" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            CSV/Excel Import
          </TabsTrigger>
          <TabsTrigger value="bulk" className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Bulk Entry
          </TabsTrigger>
          <TabsTrigger value="export" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Data
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="import">
          <CSVImportSection getAuthHeaders={getAuthHeaders} />
        </TabsContent>

        <TabsContent value="bulk">
          <BulkEntrySection getAuthHeaders={getAuthHeaders} />
        </TabsContent>

        <TabsContent value="export">
          <ExportDataSection getAuthHeaders={getAuthHeaders} />
        </TabsContent>

        <TabsContent value="history">
          <ImportHistorySection getAuthHeaders={getAuthHeaders} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== CSV IMPORT SECTION ====================

const CSVImportSection = ({ getAuthHeaders }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Import
  const [dataType, setDataType] = useState('children');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const dataTypeConfig = {
    children: {
      icon: Baby,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      fields: ['first_name', 'last_name', 'date_of_birth', 'classroom', 'parent_name', 'parent_email', 'parent_phone', 'allergies', 'notes']
    },
    staff: {
      icon: Users,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      fields: ['first_name', 'last_name', 'email', 'role', 'phone', 'hire_date', 'certifications']
    },
    billing: {
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      fields: ['child_name', 'parent_name', 'amount', 'date', 'description', 'status']
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewData(null);
      setColumnMapping({});
      setImportResult(null);
    }
  };

  const handleUploadPreview = async () => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('data_type', dataType);

      const response = await axios.post(
        `${API}/migration/upload-preview`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setPreviewData(response.data);
      setColumnMapping(response.data.mapping_suggestions || {});
      setStep(2);
      toast.success(`Found ${response.data.total_rows} rows to import`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process file');
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('data_type', dataType);
      formData.append('column_mapping', JSON.stringify(columnMapping));

      const response = await axios.post(
        `${API}/migration/import`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResult(response.data);
      setStep(3);
      toast.success(`Successfully imported ${response.data.imported} records`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/migration/template/${dataType}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_import_template.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetImport = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setColumnMapping({});
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const config = dataTypeConfig[dataType];
  const Icon = config.icon;

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-4 mb-8">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 ${step >= s ? 'text-amber-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-amber-100 text-amber-600' : 'bg-gray-100'
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              <span className="font-medium">
                {s === 1 ? 'Upload' : s === 2 ? 'Map Columns' : 'Complete'}
              </span>
            </div>
            {s < 3 && <ArrowRight className="w-4 h-4 text-gray-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Your Data File</CardTitle>
            <CardDescription>
              Upload a CSV or Excel file to import data. Download a template to see the expected format.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Data Type Selection */}
            <div className="space-y-2">
              <Label>What type of data are you importing?</Label>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(dataTypeConfig).map(([type, cfg]) => {
                  const TypeIcon = cfg.icon;
                  return (
                    <button
                      key={type}
                      onClick={() => setDataType(type)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        dataType === type 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      <TypeIcon className={`w-8 h-8 mx-auto mb-2 ${cfg.color}`} />
                      <p className="font-medium capitalize">{type}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Select File (CSV or Excel)</Label>
              <div className="flex gap-4">
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className={`flex items-center justify-center gap-2 p-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      file ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-amber-400'
                    }`}
                  >
                    {file ? (
                      <>
                        <FileSpreadsheet className="w-8 h-8 text-green-500" />
                        <div className="text-left">
                          <p className="font-medium">{file.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400" />
                        <div className="text-left">
                          <p className="font-medium">Click to upload</p>
                          <p className="text-sm text-muted-foreground">CSV or Excel files</p>
                        </div>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={downloadTemplate}>
                <FileDown className="w-4 h-4 mr-2" />
                Download Template
              </Button>
              <Button onClick={handleUploadPreview} disabled={!file || uploading}>
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Map Columns */}
      {step === 2 && previewData && (
        <Card>
          <CardHeader>
            <CardTitle>Map Your Columns</CardTitle>
            <CardDescription>
              Match your file columns to the system fields. We've suggested some mappings based on column names.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Column Mapping */}
            <div className="grid grid-cols-2 gap-4">
              {config.fields.map((field) => (
                <div key={field} className="space-y-1">
                  <Label className="capitalize">{field.replace(/_/g, ' ')}</Label>
                  <Select
                    value={columnMapping[field] || ''}
                    onValueChange={(value) => setColumnMapping({...columnMapping, [field]: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">-- Skip --</SelectItem>
                      {previewData.columns.map((col) => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Preview Table */}
            <div className="space-y-2">
              <Label>Data Preview (First 5 rows)</Label>
              <ScrollArea className="h-48 border rounded-lg">
                <table className="mobile-cards w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      {previewData.columns.slice(0, 6).map((col) => (
                        <th key={col} className="px-3 py-2 text-left font-medium">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.preview_data.slice(0, 5).map((row, i) => (
                      <tr key={i} className="border-t">
                        {previewData.columns.slice(0, 6).map((col) => (
                          <td key={col} className="px-3 py-2 truncate max-w-[150px]">
                            {row[col] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
              <p className="text-xs text-muted-foreground">
                Total rows to import: {previewData.total_rows}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={handleImport} disabled={importing}>
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {previewData.total_rows} Records
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Complete */}
      {step === 3 && importResult && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-full">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <CardTitle>Import Complete!</CardTitle>
                <CardDescription>Your data has been successfully imported</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-green-600">{importResult.imported}</p>
                <p className="text-sm text-muted-foreground">Imported</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-yellow-600">{importResult.skipped}</p>
                <p className="text-sm text-muted-foreground">Skipped</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-red-600">{importResult.errors?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Errors</p>
              </div>
            </div>

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="space-y-2">
                <Label>Errors</Label>
                <ScrollArea className="h-32 border rounded-lg p-3 bg-red-50">
                  {importResult.errors.map((error, i) => (
                    <p key={i} className="text-sm text-red-600">{error}</p>
                  ))}
                </ScrollArea>
              </div>
            )}

            {importResult.note && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">{importResult.note}</p>
              </div>
            )}

            <Button onClick={resetImport} className="w-full">
              Import More Data
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== BULK ENTRY SECTION ====================

const BulkEntrySection = ({ getAuthHeaders }) => {
  const [dataType, setDataType] = useState('children');
  const [entries, setEntries] = useState([{}]);
  const [submitting, setSubmitting] = useState(false);

  const childFields = [
    { key: 'first_name', label: 'First Name', required: true },
    { key: 'last_name', label: 'Last Name', required: true },
    { key: 'date_of_birth', label: 'DOB', type: 'date' },
    { key: 'classroom', label: 'Classroom' },
    { key: 'parent_name', label: 'Parent Name' },
    { key: 'parent_email', label: 'Parent Email', type: 'email' },
    { key: 'parent_phone', label: 'Parent Phone' },
  ];

  const staffFields = [
    { key: 'first_name', label: 'First Name', required: true },
    { key: 'last_name', label: 'Last Name', required: true },
    { key: 'email', label: 'Email', type: 'email', required: true },
    { key: 'role', label: 'Role' },
    { key: 'phone', label: 'Phone' },
    { key: 'hire_date', label: 'Hire Date', type: 'date' },
  ];

  const fields = dataType === 'children' ? childFields : staffFields;

  const addEntry = () => {
    setEntries([...entries, {}]);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== index));
    }
  };

  const updateEntry = (index, field, value) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    setEntries(updated);
  };

  const handleSubmit = async () => {
    const validEntries = entries.filter(e => e.first_name && e.last_name);
    if (validEntries.length === 0) {
      toast.error('Please fill in at least one complete entry');
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = dataType === 'children' 
        ? `${API}/migration/bulk-add-children`
        : `${API}/migration/bulk-add-staff`;

      const response = await axios.post(endpoint, validEntries, getAuthHeaders());
      
      toast.success(`Successfully added ${response.data.imported} ${dataType}`);
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }

      // Show temp passwords for staff
      if (dataType === 'staff' && response.data.created_accounts?.length > 0) {
        console.log('Created accounts:', response.data.created_accounts);
        toast.info('Check console for temporary passwords');
      }

      setEntries([{}]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entries');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Data Type Toggle */}
      <div className="flex gap-4">
        <Button
          variant={dataType === 'children' ? 'default' : 'outline'}
          onClick={() => { setDataType('children'); setEntries([{}]); }}
        >
          <Baby className="w-4 h-4 mr-2" />
          Add Children
        </Button>
        <Button
          variant={dataType === 'staff' ? 'default' : 'outline'}
          onClick={() => { setDataType('staff'); setEntries([{}]); }}
        >
          <Users className="w-4 h-4 mr-2" />
          Add Staff
        </Button>
      </div>

      {/* Entry Cards */}
      <div className="space-y-4">
        {entries.map((entry, index) => (
          <Card key={index}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {dataType === 'children' ? 'Child' : 'Staff Member'} #{index + 1}
                </CardTitle>
                {entries.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeEntry(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {fields.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-xs">
                      {field.label}
                      {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      type={field.type || 'text'}
                      value={entry[field.key] || ''}
                      onChange={(e) => updateEntry(index, field.key, e.target.value)}
                      placeholder={field.label}
                      className="h-9"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={addEntry}>
          <Plus className="w-4 h-4 mr-2" />
          Add Another
        </Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Check className="w-4 h-4 mr-2" />
          )}
          Save All ({entries.filter(e => e.first_name && e.last_name).length})
        </Button>
      </div>
    </div>
  );
};

// ==================== IMPORT HISTORY SECTION ====================

const ImportHistorySection = ({ getAuthHeaders }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/migration/history`, getAuthHeaders());
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-muted-foreground">No import history yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {history.map((item) => (
        <Card key={item.id}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${
                  item.data_type === 'children' ? 'bg-blue-100' :
                  item.data_type === 'staff' ? 'bg-purple-100' : 'bg-green-100'
                }`}>
                  {item.data_type === 'children' ? <Baby className="w-5 h-5 text-blue-600" /> :
                   item.data_type === 'staff' ? <Users className="w-5 h-5 text-purple-600" /> :
                   <DollarSign className="w-5 h-5 text-green-600" />}
                </div>
                <div>
                  <p className="font-medium">{item.filename}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(item.imported_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  {item.imported} imported
                </Badge>
                {item.skipped > 0 && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                    {item.skipped} skipped
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ==================== EXPORT DATA SECTION ====================

const ExportDataSection = ({ getAuthHeaders }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const response = await axios.get(`${API}/migration/export-summary`, getAuthHeaders());
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load export summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (dataType, format = 'csv') => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${API}/migration/export/${dataType}?format=${format}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const contentType = response.headers['content-type'];
      const isZip = contentType?.includes('zip');
      const isExcel = contentType?.includes('spreadsheet');
      
      const ext = isZip ? 'zip' : isExcel ? 'xlsx' : 'csv';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_export_${new Date().toISOString().split('T')[0]}.${ext}`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${dataType} data exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${dataType} data`);
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  const exportOptions = [
    {
      type: 'children',
      icon: Baby,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      title: 'Children',
      description: 'Export all children records with parent info',
      count: summary?.children || 0
    },
    {
      type: 'staff',
      icon: Users,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      title: 'Staff',
      description: 'Export staff records (excludes passwords)',
      count: summary?.staff || 0
    },
    {
      type: 'billing',
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      title: 'Billing',
      description: 'Export invoices and billing history',
      count: summary?.billing || 0
    }
  ];

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Data Portability</p>
              <p className="text-sm text-amber-700">
                Export your data anytime. Your data belongs to you - download it in CSV or Excel format 
                to migrate to another system or keep as backup.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export All Button */}
      <Card className="border-2 border-dashed border-amber-300 bg-amber-50/50">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <FileDown className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Export All Data</h3>
                <p className="text-sm text-muted-foreground">
                  Download everything as a ZIP file ({summary?.total_records || 0} total records)
                </p>
              </div>
            </div>
            <Button 
              size="lg"
              onClick={() => handleExport('all')}
              disabled={exporting === 'all'}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {exporting === 'all' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download All Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Individual Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {exportOptions.map((option) => {
          const Icon = option.icon;
          return (
            <Card key={option.type}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${option.bgColor}`}>
                    <Icon className={`w-6 h-6 ${option.color}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{option.title}</CardTitle>
                    <p className="text-2xl font-bold">{option.count}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{option.description}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleExport(option.type, 'csv')}
                    disabled={exporting === option.type || option.count === 0}
                  >
                    {exporting === option.type ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>CSV</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleExport(option.type, 'xlsx')}
                    disabled={exporting === option.type || option.count === 0}
                  >
                    {exporting === option.type ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>Excel</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* What's Included */}
      <Card>
        <CardHeader>
          <CardTitle>What's Included in Exports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Children Export:</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Child names and dates of birth</li>
                <li>Classroom assignments</li>
                <li>Parent/guardian contact info</li>
                <li>Allergies and special notes</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Staff Export:</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Staff names and contact info</li>
                <li>Roles and hire dates</li>
                <li>Certifications</li>
                <li>Note: Passwords are never exported</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Billing Export:</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Invoice history</li>
                <li>Payment records</li>
                <li>Amounts and dates</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Full Export (ZIP) Also Includes:</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>DCF Enrollment forms data</li>
                <li>Health reports data</li>
                <li>Export summary report</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataMigrationPage;
